/* Shoe Me storefront helpers: USA batch dispatch estimate + cart drawer (replaces Multi Location Stock Info and AOV.ai Cart Drawer). */
(() => {
  'use strict';

  /* ------------------------------------------------------------------
   * USA batch estimate
   * Orders go to Pleaser USA at 10pm Monday and 10pm Friday (Sydney time).
   * Pleaser ships that US day (skipping weekends + window.ShoeMeHolidays.us),
   * DHL lands it 3 days later, and we ship from Sydney that day (skipping
   * weekends + window.ShoeMeHolidays.nsw).
   * ---------------------------------------------------------------- */
  const routeRoot = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const nativeFetch = window.fetch.bind(window);

  const CUTOFF_HOUR = 22;
  const CUTOFF_DAYS = [1, 5];
  const TRANSIT_DAYS = 3;
  const HOUR = 3600000;
  const DAY = 24 * HOUR;

  const isoDay = ms => new Date(ms).toISOString().slice(0, 10);
  const isWeekend = ms => [0, 6].includes(new Date(ms).getUTCDay());
  const toSet = value => new Set((Array.isArray(value) ? value : String(value || '').split(','))
    .map(s => String(s).trim()).filter(Boolean));
  const formatDay = (ms, weekday) => {
    // "Thursday 29 Oct" / "Thu 29 Oct" (no comma, whatever the browser locale data does)
    const parts = {};
    new Intl.DateTimeFormat('en-AU', { timeZone: 'UTC', weekday, day: 'numeric', month: 'short' })
      .formatToParts(new Date(ms)).forEach(p => { parts[p.type] = p.value; });
    return `${parts.weekday} ${parts.day} ${parts.month}`;
  };

  const sydneyNow = () => {
    const parts = {};
    new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Sydney',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
    }).formatToParts(new Date()).forEach(p => { parts[p.type] = p.value; });

    return {
      now: Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second),
      today: Date.UTC(+parts.year, +parts.month - 1, +parts.day),
    };
  };

  const estimate = (now, today, us, nsw) => {
    let cutoffDay = null;
    for (let k = 0; k < 8; k++) {
      const day = today + k * DAY;
      if (CUTOFF_DAYS.includes(new Date(day).getUTCDay()) && day + CUTOFF_HOUR * HOUR > now) {
        cutoffDay = day;
        break;
      }
    }
    if (cutoffDay === null) return null;

    let ship = cutoffDay;
    while (isWeekend(ship) || us.has(isoDay(ship))) ship += DAY;

    let dispatch = ship + TRANSIT_DAYS * DAY;
    while (isWeekend(dispatch) || nsw.has(isoDay(dispatch))) dispatch += DAY;

    return { cutoff: cutoffDay + CUTOFF_HOUR * HOUR, dispatch, adjusted: dispatch !== cutoffDay + TRANSIT_DAYS * DAY };
  };

  const batchInfo = (el, clock = sydneyNow()) => {
    const holidays = window.ShoeMeHolidays || {};
    const us = toSet((el && el.dataset.usHolidays) || holidays.us);
    const nsw = toSet((el && el.dataset.nswHolidays) || holidays.nsw);
    const result = estimate(clock.now, clock.today, us, nsw);
    if (!result) return null;

    const minutesLeft = Math.max(0, Math.floor((result.cutoff - clock.now) / 60000));
    const h = Math.floor(minutesLeft / 60);
    const m = minutesLeft % 60;
    const soon = minutesLeft < 24 * 60;
    const cutoffDay = new Date(result.cutoff).getUTCDay() === 1 ? 'Monday' : 'Friday';
    const timeLeft = `${h ? `${h}h ` : ''}${m}m`;

    return {
      ...result,
      cutoffDay,
      when: soon ? `within <strong>${timeLeft}</strong>` : `by <strong>10pm ${cutoffDay}</strong>`,
      label: formatDay(result.dispatch, 'long'),
      labelShort: formatDay(result.dispatch, 'short'),
      note: result.adjusted ? ' (allows for public holidays)' : '',
    };
  };

  const FORMATS = {
    // Product page, USA size selected
    full: b => `order ${b.when} and it ships from our Sydney warehouse <strong>${b.label}</strong>${b.note}`,
    // Product page, no size selected yet
    short: b => `ships from Sydney ${b.label}${b.note}`,
    // Cart: everything is a USA import
    usa: b => `Order ${b.when} and your order ships from our Sydney warehouse <strong>${b.label}</strong>${b.note}.`,
    // Cart: Sydney stock + USA imports, shipped together
    together: b => `Your whole order ships together from our Sydney warehouse on <strong>${b.label}</strong>${b.note}, once the USA items land. Order ${b.when} to make this batch.`,
    // Site-wide bar
    bar: b => `USA imports: order ${b.when} to ship from Sydney <strong>${b.labelShort}</strong>`,
  };

  // Saves the estimate on the cart as a note attribute, so it's on the order
  // (admin "Additional details") and can be shown in the order confirmation email.
  const ATTRIBUTE_NAME = 'Estimated dispatch';
  let lastAttribute = null;
  const syncCartAttribute = value => {
    if (value === lastAttribute) return;
    lastAttribute = value;
    nativeFetch(`${routeRoot()}cart/update.js`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { [ATTRIBUTE_NAME]: value } }),
    }).catch(() => { lastAttribute = null; });
  };

  class SydneyDispatchEstimate extends HTMLElement {
    #timer = null;

    connectedCallback() {
      this.render();
      this.#timer = setInterval(() => this.render(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.#timer);
    }

    render() {
      try {
        const b = batchInfo(this);
        if (!b) return;
        const attribute = this.dataset.cartAttribute;
        if (attribute) {
          syncCartAttribute(attribute === 'sydney'
            ? 'In stock in Sydney – ships in 1–2 business days'
            : `Ships from Sydney ${b.label} (USA import – ${b.cutoffDay} 10pm batch)`);
          if (attribute === 'sydney') return;
        }
        const format = this.dataset.format || (this.hasAttribute('data-short') ? 'short' : 'full');
        this.innerHTML = (FORMATS[format] || FORMATS.full)(b);
      } catch (error) {
        // Keep the fallback text.
      }
    }
  }

  window.ShoeMeDispatch = { estimate, batchInfo, FORMATS };
  if (!customElements.get('sydney-dispatch-estimate')) {
    customElements.define('sydney-dispatch-estimate', SydneyDispatchEstimate);
  }

  /* ------------------------------------------------------------------
   * Cart drawer (sections/shoe-me-cart-drawer.liquid)
   * ---------------------------------------------------------------- */
  const CART_ADD = /\/cart\/add(\.js)?(\?|$)/;
  const CART_MUTATE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const onCartPage = () => /\/cart\/?$/.test(location.pathname);
  const drawer = () => document.querySelector('shoe-me-cart-drawer');

  class ShoeMeCartDrawer extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', event => this.onClick(event));
      this.addEventListener('change', event => this.onChange(event));
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      });
      this.syncUpsells();
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open() {
      if (this.isOpen) return;
      this.lastFocus = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('smcd-locked');
      const panel = this.querySelector('.smcd__panel');
      if (panel) panel.focus({ preventScroll: true });
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('smcd-locked');
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus({ preventScroll: true });
    }

    get sectionId() {
      // The drawer lives in the footer section group, so its section id is generated (sections--123__key).
      const wrapper = this.closest('.shopify-section');
      return wrapper ? wrapper.id.replace(/^shopify-section-/, '') : 'shoe-me-cart-drawer';
    }

    async refresh() {
      const id = this.sectionId;
      let url = `${routeRoot()}?sections=${encodeURIComponent(id)}`;
      // Theme previews: render from the previewed theme, not the live one.
      const theme = window.Shopify && window.Shopify.theme;
      if (theme && theme.role && theme.role !== 'main') url += `&preview_theme_id=${theme.id}`;
      const response = await nativeFetch(url, { credentials: 'same-origin' });
      const json = await response.json();
      const doc = new DOMParser().parseFromString(json[id] || '', 'text/html');
      const fresh = doc.querySelector('[data-smcd-content]');
      const current = this.querySelector('[data-smcd-content]');
      if (!fresh || !current) return;
      current.replaceWith(document.importNode(fresh, true));
      this.syncUpsells();

      const count = fresh.dataset.itemCount || '0';
      document.querySelectorAll('[data-header-cart-count]').forEach(el => {
        el.textContent = count === '0' ? '' : count;
        el.classList.toggle('visible', count !== '0');
      });
    }

    async request(url, body) {
      this.classList.add('is-busy');
      this.setError('');
      try {
        const response = await nativeFetch(`${routeRoot()}${url}`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await response.json().catch(() => ({}));
        await this.refresh();
        if (!response.ok) this.setError(json.description || json.message || 'Sorry, something went wrong. Please try again.');
      } catch (error) {
        this.setError('Sorry, something went wrong. Please try again.');
      } finally {
        this.classList.remove('is-busy');
      }
    }

    setError(message) {
      const el = this.querySelector('[data-smcd-error]');
      if (!el) return;
      el.textContent = message;
      el.hidden = !message;
    }

    onClick(event) {
      const target = event.target.closest('[data-smcd-close], [data-smcd-qty], [data-smcd-remove], [data-smcd-upsell-add]');
      if (!target || this.classList.contains('is-busy')) return;
      event.preventDefault();

      if (target.matches('[data-smcd-close]')) {
        this.close();
      } else if (target.matches('[data-smcd-qty]')) {
        this.request('cart/change.js', { line: Number(target.dataset.line), quantity: Number(target.dataset.qty) });
      } else if (target.matches('[data-smcd-remove]')) {
        this.request('cart/change.js', { line: Number(target.dataset.line), quantity: 0 });
      } else if (target.matches('[data-smcd-upsell-add]')) {
        const variant = this.selectedVariant(target.closest('[data-smcd-upsell]'));
        if (variant && variant.a) this.request('cart/add.js', { items: [{ id: variant.id, quantity: 1 }] });
      }
    }

    onChange(event) {
      if (event.target.matches('[data-smcd-option]')) this.syncUpsells();
    }

    selectedVariant(card) {
      if (!card) return null;
      let variants = [];
      try {
        variants = JSON.parse(card.querySelector('[data-smcd-variants]').textContent);
      } catch (error) {
        return null;
      }
      const picks = [...card.querySelectorAll('[data-smcd-option]')]
        .map(select => ({ index: Number(select.dataset.smcdOption), value: select.value }));
      return variants.find(v => picks.every(p => v.o[p.index] === p.value)) || null;
    }

    syncUpsells() {
      this.querySelectorAll('[data-smcd-upsell]').forEach(card => {
        const button = card.querySelector('[data-smcd-upsell-add]');
        if (!button) return;
        const variant = this.selectedVariant(card);
        const available = Boolean(variant && variant.a);
        button.disabled = !available;
        button.textContent = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
      });
    }
  }

  if (!customElements.get('shoe-me-cart-drawer')) {
    customElements.define('shoe-me-cart-drawer', ShoeMeCartDrawer);
  }

  // Refresh (and on add, open) the drawer whenever anything on the page changes the cart.
  const onCartMutated = isAdd => {
    const d = drawer();
    if (!d || onCartPage()) return;
    d.refresh().then(() => { if (isAdd) d.open(); }).catch(() => {});
  };

  window.fetch = function shoeMeFetch(input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const promise = nativeFetch(input, init);
    if (CART_MUTATE.test(url)) {
      promise.then(response => { if (response.ok) onCartMutated(CART_ADD.test(url)); }).catch(() => {});
    }
    return promise;
  };

  const xhrOpen = XMLHttpRequest.prototype.open;
  const xhrSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function shoeMeOpen(method, url, ...rest) {
    this.shoeMeUrl = String(url);
    return xhrOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function shoeMeSend(...args) {
    if (CART_MUTATE.test(this.shoeMeUrl || '')) {
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) onCartMutated(CART_ADD.test(this.shoeMeUrl));
      });
    }
    return xhrSend.apply(this, args);
  };

  // Header cart icon and "View cart" links open the drawer instead of going to /cart.
  document.addEventListener('click', event => {
    const link = event.target.closest('a.site-header-cart--button, [data-atc-banner-cart-button], [data-smcd-open]');
    const d = drawer();
    if (!link || !d || onCartPage() || event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    d.open();
    d.refresh().catch(() => {});
  }, true);

  window.addEventListener('pageshow', event => {
    if (event.persisted && drawer()) drawer().refresh().catch(() => {});
  });
})();
